import React, { useState, useLayoutEffect } from "react";
import Button from "../../../ui/button/Button";
import AddTaxRates from "../../tax-components/AddTaxRates";
import CollectTaxRegistrationNumber from "../../tax-components/CollectTaxRegistrationNumber";
import TaxRegistrationNumber from "../../tax-components/TaxRegistrationNumber";
import TaxExemption from "../../tax-components/TaxExemption";
import { TaxCollection } from "../../../../models/Taxes";
import { TaxRate, ManualTaxConfig } from "../../../../utils/grpc/generated/Billsby.Protos/billing/private/company/taxation/taxation_pb";
import { useDispatch, useSelector } from "react-redux";
import { AppState } from "../../../..";
import "./TaxConfigFlow.scss";
import { deleteTaxconfig, fetchTaxConfig, updateManualTaxConfig } from "../../../../actions/companyTaxRatesActions";

interface IOtherCountriesTaxConfigFlow {
  handleUpdateCallback?: Function;
}

const OtherCountriesTaxConfigFlow: React.FC<IOtherCountriesTaxConfigFlow> = ({ handleUpdateCallback }) => {
  const auth = useSelector((state: AppState) => state.auth);
  const companyTaxRates = useSelector((state: AppState) => state.companyTaxRatesReducer);

  const dispatch = useDispatch<Function>();

  const [isLoading, setIsLoading] = useState(false);
  const [isConfigred, setIsConfigured] = useState(false);

  //Add tax states
  const [taxRates, setTaxRates] = useState<Array<TaxRate>>([]);
  const [taxName, setTaxName] = useState("");
  const [taxRate, setTaxRate] = useState(0);

  //Tax registration number
  const [taxLabel, setTaxLabel] = useState("");
  const [taxRegNumber, setTaxRegNumber] = useState("");

  //Collect tax registration number states
  const [taxRegNumberRequirementType, setTaxRegNumberRequirementType] = useState<TaxCollection>(TaxCollection.DO_NOT_COLLECT);

  //Customer tax exemption
  const [customerTaxExemption, setCustomerTaxExemption] = useState(false);

  const { currentCompanyDomain } = auth;
  const { currentCountry } = companyTaxRates;


  useLayoutEffect(() => {
    //Preload data if configured

    if (currentCountry && currentCountry.taxConfig) {

      const taxConfig = currentCountry.taxConfig as ManualTaxConfig.AsObject;
      setIsConfigured(true);

      setTaxRates(
        taxConfig.taxRatesList.map((taxRate) => {
          const tr = new TaxRate();
          tr.setName(taxRate.name);
          tr.setRate(taxRate.rate);
          return tr;
        })
      );
      setTaxLabel(taxConfig.taxLabel);
      setTaxRegNumber(taxConfig.taxRegNumber);
      setTaxRegNumberRequirementType(taxConfig.taxRegNumberRequirementType as TaxCollection);
      setCustomerTaxExemption(taxConfig.customerTaxExemption);
    }
  }, [currentCountry]);


  if (!currentCountry) {
    return null;
  }
  
  const onAdd = () => {
    const newTaxRate = new TaxRate();
    newTaxRate.setName(taxName);
    newTaxRate.setRate(taxRate);
    newTaxRate.setCountryIso3(currentCountry.countryCode);

    setTaxRates([...taxRates, newTaxRate]);
    setTaxName("");
    setTaxRate(0);
  };

  const onDelete = (i: number) => {
    setTaxRates(taxRates.filter((tax, _i) => i !== _i));
  };

  const onDeleteConfig = async () => {
    if (!currentCountry) {
      return;
    }

    try {
      //Other countries tax config has the "ManualTax" as the default tax service type
      await dispatch(deleteTaxconfig(currentCompanyDomain, currentCountry.countryCode, "ManualTax"));
      await dispatch(fetchTaxConfig(currentCompanyDomain));
      handleUpdateCallback && handleUpdateCallback();
    } catch (err) {
      console.log(err);
    }
  };

  const onUpdateTaxConfig = async () => {
    if (taxRates.length === 0) {
      return;
    }

    setIsLoading(true);
    const manualTaxConfig = new ManualTaxConfig();

    manualTaxConfig.setTaxRatesList(taxRates);
    manualTaxConfig.setTaxLabel(taxLabel);
    manualTaxConfig.setTaxRegNumber(taxRegNumber);
    manualTaxConfig.setTaxRegNumberRequirementType(taxRegNumberRequirementType);
    manualTaxConfig.setCustomerTaxExemption(taxRegNumberRequirementType !== TaxCollection.DO_NOT_COLLECT ? customerTaxExemption : false);

    try {
      await dispatch(updateManualTaxConfig(currentCompanyDomain, currentCountry.countryCode, manualTaxConfig));
      await dispatch(fetchTaxConfig(currentCompanyDomain));

      handleUpdateCallback && handleUpdateCallback();
    } catch (err) {
      setIsLoading(false);
    }
  };

  return (
    <div className="tax-config-flow">
      <AddTaxRates
        taxName={taxName}
        taxRate={taxRate}
        taxRates={taxRates}
        onChangeTaxName={(value) => setTaxName(value)}
        onChangeTaxRate={(value) => setTaxRate(value)}
        onAdd={onAdd}
        onDelete={onDelete}
        onDeleteConfig={onDeleteConfig}
        isConfigured={isConfigred}
      />

      <TaxRegistrationNumber taxLabel={taxLabel} taxRegNumber={taxRegNumber} onChangeTaxLabel={(value) => setTaxLabel(value)} onChangeTaxRegNumber={(value) => setTaxRegNumber(value)} />

      <CollectTaxRegistrationNumber
        taxRegNumberRequirementType={taxRegNumberRequirementType}
        onChangeType={(value) => setTaxRegNumberRequirementType(value)}
        onChangeCollectTax={(value) => setTaxRegNumberRequirementType(value)}
      />

      {taxRegNumberRequirementType !== TaxCollection.DO_NOT_COLLECT && (
        <TaxExemption customerTaxExemption={customerTaxExemption} onChangeTaxExemption={() => setCustomerTaxExemption(!customerTaxExemption)}></TaxExemption>
      )}

      <Button disabled={taxRates.length === 0} isLoading={isLoading} isFullWidth id="save-manual-tax" title="MANUAL_TAX_CONFIG_SAVE" onClick={onUpdateTaxConfig} />
    </div>
  );
};

export default OtherCountriesTaxConfigFlow;
